import {
  CHAT_PROMPT_CUSTOM_TYPE,
  POPULAR_PROMPTS_BY_RECORD_TYPE,
  POPULAR_PROMPTS_DEFAULT,
  filterPromptsForContext,
  isPromptUsable,
  isTranscriptSoftGatedPrompt,
} from "../constants/chatConstants";
import type { CrmRecordType, ManagingPromptCatalogEntry } from "../types/chat.types";

export interface RankedPromptSuggestion {
  prompt: ManagingPromptCatalogEntry;
  score: number;
  setupReason?: string;
}

export interface RankPromptsOptions {
  recordType?: CrmRecordType | null;
  hasTranscript?: boolean;
  /** Max items when query is non-empty (default 5). Empty query always returns up to 3. */
  maxResults?: number;
}

const HARD_GATE_PENALTY = 1000;
const RECORD_TYPE_BOOST = 12;

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length > 0);
}

function scorePromptForQuery(
  prompt: ManagingPromptCatalogEntry,
  queryTokens: string[],
  recordType?: CrmRecordType | null
): number {
  if (queryTokens.length === 0) {
    return 0;
  }

  const fields: Array<{ text: string; weight: number }> = [
    { text: prompt.name, weight: 4 },
    { text: prompt.promptType, weight: 3 },
    { text: prompt.description, weight: 2 },
    { text: (prompt.categories ?? []).join(" "), weight: 1 },
  ];

  let score = 0;
  for (const { text, weight } of fields) {
    const haystack = text.toLowerCase();
    const fieldTokens = tokenize(text);
    for (const token of queryTokens) {
      if (haystack.includes(token)) {
        score += weight;
      }
      if (fieldTokens.some((ft) => ft.includes(token) || token.includes(ft))) {
        score += weight * 0.5;
      }
    }
  }

  if (recordType && prompt.recordTypes?.includes(recordType)) {
    score += RECORD_TYPE_BOOST;
  }

  const softTranscriptGate = isTranscriptSoftGatedPrompt(prompt);
  if (prompt.runnable === false && !softTranscriptGate) {
    score -= HARD_GATE_PENALTY;
  }

  return score;
}

function resolvePopularPromptTypes(recordType?: CrmRecordType | null): readonly string[] {
  if (recordType && POPULAR_PROMPTS_BY_RECORD_TYPE[recordType]?.length) {
    return POPULAR_PROMPTS_BY_RECORD_TYPE[recordType]!;
  }
  return POPULAR_PROMPTS_DEFAULT;
}

function rankPopularPrompts(
  catalog: ManagingPromptCatalogEntry[],
  recordType?: CrmRecordType | null,
  hasTranscript?: boolean
): RankedPromptSuggestion[] {
  const filtered = filterPromptsForContext(catalog, { recordType }).filter(
    (p) => p.promptType !== CHAT_PROMPT_CUSTOM_TYPE
  );
  const byType = new Map(filtered.map((p) => [p.promptType, p]));
  const popularTypes = resolvePopularPromptTypes(recordType);

  const ranked: RankedPromptSuggestion[] = [];
  for (const promptType of popularTypes) {
    const prompt = byType.get(promptType);
    if (!prompt) continue;
    const usability = isPromptUsable(prompt, recordType, {
      hasCallRecordingWithTranscript: hasTranscript,
    });
    ranked.push({
      prompt,
      score: 0,
      setupReason: usability.setupReason,
    });
    if (ranked.length >= 3) break;
  }

  if (ranked.length >= 3) {
    return ranked;
  }

  for (const prompt of filtered) {
    if (ranked.some((r) => r.prompt.promptType === prompt.promptType)) continue;
    const usability = isPromptUsable(prompt, recordType, {
      hasCallRecordingWithTranscript: hasTranscript,
    });
    if (usability.usable === false && !usability.setupReason) continue;
    ranked.push({
      prompt,
      score: 0,
      setupReason: usability.setupReason,
    });
    if (ranked.length >= 3) break;
  }

  return ranked;
}

/**
 * Rank catalog prompts for intent suggestions (story 3.51).
 * Empty query → top 3 popular prompts for record type; otherwise token overlap (max 5).
 */
export function rankPromptsForQuery(
  catalog: ManagingPromptCatalogEntry[],
  query: string,
  opts?: RankPromptsOptions
): RankedPromptSuggestion[] {
  const recordType = opts?.recordType ?? null;
  const hasTranscript = opts?.hasTranscript ?? false;
  const trimmed = query.trim();

  if (!trimmed) {
    return rankPopularPrompts(catalog, recordType, hasTranscript);
  }

  const maxResults = opts?.maxResults ?? 5;
  const queryTokens = tokenize(trimmed);
  const filtered = filterPromptsForContext(catalog, { recordType }).filter(
    (p) => p.promptType !== CHAT_PROMPT_CUSTOM_TYPE
  );

  const scored = filtered
    .map((prompt) => {
      const usability = isPromptUsable(prompt, recordType, {
        hasCallRecordingWithTranscript: hasTranscript,
      });
      return {
        prompt,
        score: scorePromptForQuery(prompt, queryTokens, recordType),
        setupReason: usability.setupReason,
        usable: usability.usable,
      };
    })
    .filter((entry) => entry.score > 0 || entry.usable || entry.setupReason)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, maxResults).map(({ prompt, score, setupReason }) => ({
    prompt,
    score,
    setupReason,
  }));
}
